import { Observable, throwError, timer } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { ClientReportService, PDF_FAILED_MESSAGE } from '../_services/client-report.service';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { ClientReportFilter } from '../_models/reports/client-report-filter.model';
import { toDateOnly } from './client-report-filter.util';

/** How often a background PDF is checked while the screen waits for it. */
export const PDF_POLL_MS = 5000;

/**
 * Downloads a native client report as Excel or PDF, for the Reports screens (View, and the combined page). A PDF large
 * enough to be prepared in the background (reports.pdf-background) comes back as a job: `preparing` is true while it
 * is polled, and the file downloads when it is ready. A refusal (too large) or a failure shows the API's own message.
 */
export class ClientReportDownload {
  /** A PDF is being prepared in the background; the screen says so until it downloads. */
  public preparing = false;
  /** How often the background PDF is checked; a spec shortens it. */
  public pollMs = PDF_POLL_MS;

  constructor(
    private readonly reports: ClientReportService,
    private readonly errors: ErrorModalHelperService,
    private readonly until: Observable<void>
  ) { }

  public excel(code: string, reportFilter: ClientReportFilter): void {
    this.reports.exportReport(code, reportFilter).subscribe((blob: Blob) => saveFile(blob, code, 'xlsx'), error => {
      this.errors.show('An error has occurred, please try again', error);
      console.error(error);
    });
  }

  public pdf(code: string, reportFilter: ClientReportFilter): void {
    if (this.preparing) { return; }
    this.reports.exportPdf(code, reportFilter).pipe(takeUntil(this.until)).subscribe({
      next: result => {
        if (result.file) {
          saveFile(result.file, code, 'pdf');
        } else if (result.job) {
          this.preparing = true;
          this.waitFor(result.job.jobId, code);
        }
      },
      error: error => this.showPdfError(error?.message),
    });
  }

  /** Polls a background PDF until it is ready (then downloads it) or has failed (then says why). */
  private waitFor(jobId: number, code: string): void {
    timer(this.pollMs, this.pollMs).pipe(
      switchMap(() => this.reports.getPdfJob(jobId)),
      filter(job => job?.status === 'Done' || job?.status === 'Failed'),
      take(1),
      switchMap(job => job.status === 'Done'
        ? this.reports.downloadGeneratedReport(job.generatedReportId)
        : throwError(() => ({ message: job.message }))),
      takeUntil(this.until)
    ).subscribe({
      next: blob => {
        this.preparing = false;
        saveFile(blob, code, 'pdf');
      },
      error: error => {
        this.preparing = false;
        this.showPdfError(error?.message);
      },
    });
  }

  private showPdfError(message: string): void {
    this.errors.show(message || PDF_FAILED_MESSAGE, { isValidationError: true });
  }
}

/** Hands a downloaded report to the browser as "<code>-<date>.<extension>". */
export function saveFile(blob: Blob, code: string, extension: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = code + '-' + toDateOnly(new Date()) + '.' + extension;
  link.click();
  URL.revokeObjectURL(url);
}
